import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as CreditCardIcon } from '../../../src/assets/icon/credit-card.svg';
import { ReactComponent as MobileCardIcon } from '../../../src/assets/icon/mobile-payment-card.svg';
import { ReactComponent as Vector } from '../../../src/assets/icon/vector838.svg';
import Toast from '../../components/Toast';
import { BillStatus, OrderItemStatus, PaymentMethod, SHIPPING_FEE } from '../../consts';
import { DownloadTicketProvider, useDownloadTicketContext } from '../../context/DownloadTicketContext';
import { DownloadTicketOrder, DownloadTicketRestaurant, FoodItem } from '../../interfaces/types';
import { formatPriceItem } from '../../utils';
import FoodDetails from '../OrderDetailV2/components/FoodItem';

const PDF_CANVAS_SCALE = 1.5;
const PDF_IMAGE_QUALITY = 0.9;

const DownloadTicketContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const { t } = useTranslation();

  const [openToast, setOpenToast] = React.useState(false);
  const toast = useRef<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });

  const { order: ctxOrder, loading: ctxLoading, fetchOrderById, getFormattedSeatById } = useDownloadTicketContext();

  const [formattedSeat, setFormattedSeat] = React.useState<string>('');
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState(false);

  const orderDetail = React.useMemo<DownloadTicketOrder | null>(() => {
    if (!orderId) return null;
    if (ctxOrder && (ctxOrder.id === orderId || ctxOrder.order_id === orderId)) {
      const o = ctxOrder as DownloadTicketOrder;
      return {
        ...ctxOrder,
        restaurant: o.restaurant ?? undefined,
        stadium_profile_pic: o.stadium_profile_pic ?? '',
        registration_code: o.registration_code ?? '',
        business_name: o.business_name ?? '',
        name: o.name ?? '',
        rider_contact: o.rider_contact ?? '',
        stadium_phone: o.stadium_phone ?? '',
        address: o.address ?? '',
      };
    }
    return null;
  }, [ctxOrder, orderId]);

  React.useEffect(() => {
    let mounted = true;
    const seatId = orderDetail?.order_table_number;
    if (!seatId) {
      setFormattedSeat('');
      return;
    }

    (async () => {
      try {
        const s = await getFormattedSeatById?.(seatId);
        if (!mounted) return;
        setFormattedSeat(s || '');
      } catch (e) {
        if (!mounted) return;
        setFormattedSeat('');
      }
    })();

    return () => {
      mounted = false;
    };
  }, [orderDetail?.order_table_number, getFormattedSeatById]);

  const price = useMemo(() => {
    if (!orderDetail) return { totalPrice: '0', totalItem: 0, totalPayment: '0', shippingFee: 0 };

    const totalItem =
      (orderDetail.foods?.reduce(
        (total: number, food: FoodItem) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
        0
      ) || 0) +
      (orderDetail.promotion_foods?.reduce(
        (total: number, food: FoodItem) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
        0
      ) || 0);

    const shippingFee = orderDetail.order_status === OrderItemStatus.CANCELLED ? 0 : totalItem === 0 ? 0 : SHIPPING_FEE;

    const calculateTotal = (foods: FoodItem[]) => {
      return (
        foods?.reduce((total: number, food: FoodItem) => {
          const actualQuantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
          return total + actualQuantity * food.item_price;
        }, 0) || 0
      );
    };

    const totalPrice = calculateTotal(orderDetail.foods) + calculateTotal(orderDetail.promotion_foods);
    const totalPayment = totalPrice + shippingFee;

    return {
      totalPrice: totalPrice.toLocaleString('ja-JP'),
      totalItem,
      totalPayment: totalPayment.toLocaleString('ja-JP'),
      shippingFee,
    };
  }, [orderDetail]);

  const taxSummary = useMemo(() => {
    if (!orderDetail) return null;
    const foods = [...(orderDetail.foods || []), ...(orderDetail.promotion_foods || [])];
    const totalItem =
      (orderDetail.foods?.reduce(
        (total: number, food: FoodItem) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
        0
      ) || 0) +
      (orderDetail.promotion_foods?.reduce(
        (total: number, food: FoodItem) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
        0
      ) || 0);
    const shippingFee = orderDetail.order_status === OrderItemStatus.CANCELLED ? 0 : totalItem === 0 ? 0 : SHIPPING_FEE;
    let totalTax10 = shippingFee;
    let totalTax8 = 0;
    let totalTaxAmount10 = 0;
    let totalTaxAmount8 = 0;

    foods.forEach((item: any) => {
      const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
      const itemPrice = quantity * item.item_price;
      const taxRate = parseFloat(item.tax_rate) || 8;
      const totalInclusive = itemPrice;
      let taxAmount = 0;
      if (totalInclusive > 0 && taxRate > 0) {
        const beforeTax = Math.round(totalInclusive / (1 + taxRate / 100));
        taxAmount = totalInclusive - beforeTax;
      }
      if (taxRate === 10) {
        totalTaxAmount10 += taxAmount;
        totalTax10 += totalInclusive;
      } else {
        totalTaxAmount8 += taxAmount;
        totalTax8 += totalInclusive;
      }
    });

    if (shippingFee > 0) {
      const beforeTax = Math.round(shippingFee / (1 + 10 / 100));
      const shippingTax = shippingFee - beforeTax;
      totalTaxAmount10 += shippingTax;
    }

    return {
      totalTax10,
      totalTax8,
      totalTaxAmount10: totalTaxAmount10 > 0 ? totalTaxAmount10 : 0,
      totalTaxAmount8: totalTaxAmount8 > 0 ? totalTaxAmount8 : 0,
    };
  }, [orderDetail]);

  const isPartiallyCancelled = useMemo(() => {
    const foods = orderDetail?.foods ?? [];
    const totalItemQuantity = foods.reduce((total: number, item: FoodItem) => total + item.item_quantity, 0);
    const totalDeletedQuantity = foods.reduce((total: number, item: FoodItem) => total + (item.item_deleted_quantity ?? 0), 0);

    return totalItemQuantity > totalDeletedQuantity && totalDeletedQuantity > 0;
  }, [orderDetail]);

  const calculateSubtotal = (food: FoodItem, shouldShowTwoItems?: boolean) => {
    const actualQuantity =
      (food.item_deleted_quantity !== undefined && !isPartiallyCancelled) || (food.item_deleted_quantity !== undefined && shouldShowTwoItems)
        ? food.item_quantity - food.item_deleted_quantity
        : food.item_quantity;
    return (actualQuantity * food.item_price).toFixed(2);
  };

  const totalQuantity = useMemo(() => {
    if (!orderDetail) return 0;

    const allFoods = [...(orderDetail.promotion_foods ?? []), ...(orderDetail.foods ?? [])];

    return allFoods.reduce((sum: number, food: FoodItem) => {
      const actualQuantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
      return sum + (actualQuantity || 0);
    }, 0);
  }, [orderDetail]);

  const getRequestedDeliveryDate = (order: DownloadTicketOrder | null) => {
    const ts = order?.requested_delivery_time || order?.order_delivery_time;
    return ts && (ts as any).seconds ? new Date((ts as any).seconds * 1000) : null;
  };

  const formatJapaneseDateOnly = (date: Date | null) => {
    if (!date) return '';
    const formatter = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    const parts = Object.fromEntries(formatter.formatToParts(date).map((p) => [p.type, p.value]));
    return `${parts.year}年${parts.month}月${parts.day}日`;
  };

  const requestedDeliveryDate = React.useMemo(() => getRequestedDeliveryDate(orderDetail), [orderDetail]);

  const generatePdf = React.useCallback(async () => {
    try {
      const node = ticketRef.current || document.getElementById('download-ticket-root');
      if (!node) return;

      const inlineImages = async (root: HTMLElement) => {
        const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
        await Promise.all(
          imgs.map(async (img) => {
            const src = img.src;
            if (!src || src.startsWith('data:')) return;
            try {
              const resp = await fetch(src, { mode: 'cors' });
              if (!resp.ok) return;
              const blob = await resp.blob();
              const dataUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
                reader.readAsDataURL(blob);
              });
              if (dataUrl) img.src = dataUrl;
            } catch (e) {
              // ignore per-image errors
            }
          })
        );
      };

      await inlineImages(node as HTMLElement);
      setIsGeneratingPdf(true);
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const canvas = await html2canvas(node, {
        scale: PDF_CANVAS_SCALE,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/jpeg', PDF_IMAGE_QUALITY);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height, undefined, 'FAST');
      const pdfName = `${orderId || orderDetail?.id}_ticket.pdf`;
      pdf.save(pdfName);
      return canvas;
    } catch (err) {
      toast.current = { type: 'error', content: t('UNABLE_TO_GENERATE_PDF') };
      setOpenToast(true);
      return null;
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [orderId, orderDetail, t]);

  const downloadedRef = React.useRef(false);
  const ticketRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!orderDetail || downloadedRef.current) return;
    downloadedRef.current = true;

    (async () => {
      setTimeout(() => {
        generatePdf().catch(() => {});
      }, 800);
    })();
  }, [orderDetail, orderId, generatePdf]);

  useEffect(() => {
    if (!orderId) return;

    if (!ctxOrder && !ctxLoading) {
      fetchOrderById(orderId).catch(() => {});
    }
  }, [orderId, ctxOrder, ctxLoading, fetchOrderById]);

  if (ctxLoading) {
    return (
      <div className="w-full min-h-[100vh] flex items-center justify-center p-6">
        <div className="w-full max-w-2xl animate-pulse">
          <div className="h-48 bg-gray-200 rounded-lg mb-4" />
          <div className="h-6 bg-gray-200 rounded mb-2 w-1/3" />
          <div className="h-4 bg-gray-200 rounded mb-6 w-1/4" />

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="h-24 bg-gray-200 rounded" />
            <div className="h-24 bg-gray-200 rounded" />
          </div>

          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="download-ticket-root" ref={ticketRef} className="w-[auto] min-h-[100vh]">
      {orderDetail && (
        <div className="max-w-[360px] m-auto flex justify-center items-center flex-col gap-2 px-4 py-6 bg-gray-50">
          <img
            src={
              orderDetail?.stadium_profile_pic ||
              'https://firebasestorage.googleapis.com/v0/b/meny-stag.appspot.com/o/stadium_info%2F00OoID7nZI8X0tLmC3bd%2Fstadium_profile?alt=media&token=406c9968-7d64-4096-9d3b-12e6349b4b82'
            }
            alt={'stadium'}
            className="max-w-[200px] h-[190px] shrink-0 rounded-lg w-auto"
          />

          <div className="flex justify-center items-center w-full flex-col pb-3 border-b border-[#EAECF0]">
            <p className="text-gray-500 font-medium text-sm leading-5">{t('RECEIPT_TITLE')}</p>
            <p className="text-gray-500 font-medium text-sm leading-5">{orderDetail?.address || '-'}</p>
            <p className="text-gray-500 font-medium text-sm leading-5">
              {t('TEL_LABEL')} {orderDetail?.stadium_phone || '-'}
            </p>
          </div>

          <div className="border-b w-full pb-4">
            <div className="my-3 p-3 flex flex-col rounded-lg border border-dashed border-neutral-300 bg-[#F9FAFB]">
              <p className="text-gray-400 font-medium text-sm leading-5">
                {t('REGISTRATION_NUMBER_LABEL')}：<span className="text-gray-800 pl-2">{orderDetail?.registration_code || '-'}</span>
              </p>
              <p className="text-gray-400 font-medium text-sm leading-5">
                {t('BUSINESS_NAME_LABEL')}：<span className="text-gray-800 pl-2">{orderDetail?.business_name || '-'}</span>
              </p>
              <p className="text-gray-800 font-medium text-sm leading-5">
                {orderDetail?.order_create_time ? new Date(orderDetail.order_create_time.seconds * 1000).toLocaleString('ja-JP') : '-'}
              </p>
              <p className="text-gray-800 font-medium text-sm leading-5">
                {orderDetail?.delivery_request === 'WHEN_ITS_READY'
                  ? '指定なし'
                  : orderDetail?.delivery_request
                    ? `${formatJapaneseDateOnly(requestedDeliveryDate)} ${orderDetail.delivery_request}`.trim()
                    : orderDetail?.order_delivery_time
                      ? new Date(orderDetail.order_delivery_time.seconds * 1000).toLocaleString('ja-JP')
                      : '-'}
              </p>
            </div>
          </div>

          <div className="w-full my-3 p-3 flex gap-3 rounded-lg border border-dashed border-neutral-300  bg-[#F9FAFB]">
            <div className="flex flex-col gap-1 flex-1">
              <p className="text-black-500 font-medium text-sm leading-5">{t('ORDER_STATUS_TITLE')}</p>
              {Array.isArray(orderDetail?.restaurant) && orderDetail.restaurant.length > 0 ? (
                orderDetail.restaurant.map((r: DownloadTicketRestaurant, idx: number) => (
                  <p key={idx} className="text-black-800 font-medium text-sm leading-5">
                    {r?.name ?? '-'}
                  </p>
                ))
              ) : (
                <p className="text-black-800 font-medium text-sm leading-5">-</p>
              )}
            </div>
            <Vector className="h-full" />
            <div className="flex flex-col gap-1 flex-1">
              <p className="text-black-500 font-medium text-sm leading-5">{t('ORDER_STATUS_SEAT_NUMBER')}</p>
              <p className="text-black-800 font-medium text-sm leading-5">{formattedSeat || '-'}</p>
            </div>
          </div>

          <div className="w-full flex justify-start text-sm leading-5  border-t pt-3 border-[#EAECF0]">
            <p className="text-black-500 font-medium border-r border-r-[#EAECF0] pr-2">{t('DELIVERY_ORDER_SUMMARY')}</p>
            <p className="text-black-500 font-medium pl-2">
              {totalQuantity} {t('MENU_CARD_ITEMS')}
            </p>
          </div>

          <div className="w-full flex flex-col gap-3">
            {[...(orderDetail?.foods || []), ...(orderDetail?.promotion_foods || [])].map((item: FoodItem, index: number) => (
              <div key={index}>
                <FoodDetails item={item} t={t} calculateSubtotal={calculateSubtotal} usePaddingAll={false} useManualStrike={isGeneratingPdf} />
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center justify-center w-full">
            <div className="py-3 w-full">
              <p className="text-gray-500 text-sm font-medium leading-5">{t('DELIVERY_ORDER_PAYMENT')}</p>
            </div>
            <div className="flex justify-between py-2 w-full">
              <p className="text-gray-700 text-xs font-normal leading-[18px]">{t('ORDER_PRICE')}</p>
              <p className="text-black-500 text-xs font-normal leading-[18px]">¥ {formatPriceItem(price?.totalPrice)}</p>
            </div>
            <div className="flex justify-between py-2 w-full">
              <p className="text-gray-700 text-xs font-normal leading-[18px]">{t('ORDER_DELIVERY_FEE')}</p>
              <p className="text-black-500 text-xs font-normal leading-[18px]">¥ {formatPriceItem(price?.shippingFee)}</p>
            </div>
            <div className="flex justify-between py-2 border-b w-full">
              <p className="text-gray-700 text-xs font-normal leading-[18px]">{t('ORDER_PAYMENT_METHOD')}</p>
              {orderDetail?.bill_status === BillStatus.CLEARED && (
                <div className="flex gap-1 items-center">
                  {orderDetail?.payment_method === PaymentMethod.Credit ? <CreditCardIcon /> : <MobileCardIcon />}
                  <p className="text-black-500 text-xs font-normal leading-[18px]">
                    {orderDetail?.payment_method === PaymentMethod.Credit ? t(PaymentMethod.Credit) : t(orderDetail?.payment_method)}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="w-full py-4 flex justify-between text-black-800 text-sm font-semibold leading-5">
            <p>{t('ORDER_TOTAL_PAYMENT')}</p>
            <p>¥ {formatPriceItem(price?.totalPayment)}</p>
          </div>

          {taxSummary && (
            <div className="w-full">
              {(taxSummary.totalTax10 > 0 || taxSummary.totalTax8 === 0) && (
                <div>
                  <div className="flex justify-between items-center text-sm mb-1">
                    <div className="text-gray-600">{t('RECEIPT_TAX_10_TOTAL')}</div>
                    <div className="text-gray-800">¥ {formatPriceItem(taxSummary.totalTax10)}</div>
                  </div>
                  {(taxSummary.totalTaxAmount10 > 0 || taxSummary.totalTaxAmount8 === 0) && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="text-gray-500">&nbsp;&nbsp;({t('RECEIPT_TAX_INCLUSIVE')}</div>
                      <div className="text-gray-800">¥ {formatPriceItem(taxSummary.totalTaxAmount10)})</div>
                    </div>
                  )}
                </div>
              )}

              {taxSummary.totalTax8 > 0 && (
                <div>
                  <div className="flex justify-between items-center text-sm mb-1">
                    <div className="text-gray-600">{t('RECEIPT_TAX_8_TOTAL')}</div>
                    <div className="text-gray-800">¥ {formatPriceItem(taxSummary.totalTax8)}</div>
                  </div>
                  {taxSummary.totalTaxAmount8 > 0 && (
                    <div className="flex justify-between items-center text-sm mb-2">
                      <div className="text-gray-500">&nbsp;&nbsp;({t('RECEIPT_TAX_INCLUSIVE')}</div>
                      <div className="text-gray-800">¥ {formatPriceItem(taxSummary.totalTaxAmount8)})</div>
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-2">{t('RECEIPT_TAX_NOTE_8')}</div>
                </div>
              )}
            </div>
          )}

          <div className="py-3 flex flex-col items-start justify-center gap-1 w-full">
            <p className="text-xs text-gray-500">
              {t('STADIUM_NAME')}: {orderDetail?.name}
            </p>
            <p className="text-xs text-gray-500">
              {t('STADIUM_CONTACT')}: {orderDetail?.email_admin || '-'}
            </p>
          </div>

          <div className="px-4 py-3 flex flex-col items-center justify-center gap-1 rounded-lg bg-green-50 w-full">
            <p className="text-black-500 font-medium text-sm leading-5">{t('HOME_ORDERID_TITLE')}</p>
            <p className="text-black-800 font-semibold text-2xl leading-[38px]">{orderId}</p>
          </div>

          <div className="w-full flex items-center justify-center gap-4 p-3 rounded-lg border border-[#D0D5DD] bg-white">
            <div className="flex flex-col items-start flex-1">
              <p className="text-gray-500 font-medium text-xs leading-4 text-left">{t('DELIVERY_CONTACT_LABEL')}</p>
              <p className="text-gray-800 font-medium text-sm leading-5 text-left">{orderDetail?.rider_contact || '-'}</p>
            </div>

            <div className="w-px self-stretch bg-[#D0D5DD]"></div>

            <div className="flex w-[40px] h-[40px] p-2.5 items-center justify-center rounded-full bg-[#E2F6E4]">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M6.98356 7.37779C7.56356 8.58581 8.35422 9.71801 9.35553 10.7193C10.3568 11.7206 11.4891 12.5113 12.6971 13.0913C12.801 13.1412 12.8529 13.1661 12.9187 13.1853C13.1523 13.2534 13.4392 13.2045 13.637 13.0628C13.6927 13.0229 13.7403 12.9753 13.8356 12.88C14.1269 12.5887 14.2726 12.443 14.4191 12.3478C14.9715 11.9886 15.6837 11.9886 16.2361 12.3478C16.3825 12.443 16.5282 12.5887 16.8196 12.88L16.9819 13.0424C17.4248 13.4853 17.6462 13.7067 17.7665 13.9446C18.0058 14.4175 18.0058 14.9761 17.7665 15.449C17.6462 15.6869 17.4248 15.9083 16.9819 16.3512L16.8506 16.4825C16.4092 16.9239 16.1886 17.1446 15.8885 17.3131C15.5556 17.5001 15.0385 17.6346 14.6567 17.6334C14.3126 17.6324 14.0774 17.5657 13.607 17.4322C11.0792 16.7147 8.69387 15.361 6.70388 13.371C4.7139 11.381 3.36017 8.99569 2.6427 6.46786C2.50919 5.99749 2.44244 5.7623 2.44141 5.41818C2.44028 5.03633 2.57475 4.51925 2.76176 4.18633C2.9303 3.88631 3.15098 3.66563 3.59233 3.22428L3.72369 3.09292C4.16656 2.65005 4.388 2.42861 4.62581 2.30833C5.09878 2.0691 5.65734 2.0691 6.1303 2.30832C6.36812 2.42861 6.58955 2.65005 7.03242 3.09291L7.19481 3.25531C7.48615 3.54665 7.63182 3.69231 7.72706 3.8388C8.08622 4.3912 8.08622 5.10336 7.72706 5.65576C7.63182 5.80225 7.48615 5.94791 7.19482 6.23925C7.09955 6.33451 7.05193 6.38214 7.01206 6.43782C6.87038 6.63568 6.82146 6.92256 6.88957 7.15619C6.90873 7.22193 6.93368 7.27389 6.98356 7.37779Z"
                  stroke="#30893C"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      )}

      <Toast content={toast.current?.content} variant={toast.current?.type} open={openToast} onClose={() => setOpenToast(false)} />
    </div>
  );
};

const DownloadTicket: React.FC = () => (
  <DownloadTicketProvider>
    <DownloadTicketContent />
  </DownloadTicketProvider>
);

export default DownloadTicket;
